import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHero, Reveal } from "@/components/ui-bits";
import { usePosts } from "@/hooks/useContent";

const CATEGORIES = ["All", "Indian Food Products", "Rice", "Spices", "Tea", "Makhana", "Dehydrated Foods", "Food Powders", "International Trade", "Packaging", "Importing from India"];

export default function Insights() {
  const [category, setCategory] = useState("All");
  const { data: posts } = usePosts(category === "All" ? undefined : category);

  return (
    <>
      <Seo
        title="Orvella Global Food Trade Insights — Blog"
        description="Insights on Indian food products, rice, spices, tea, makhana, dehydrated foods, food powders, packaging and importing from India — for international B2B buyers."
        path="/insights"
      />

      <PageHero
        eyebrow="Insights"
        title="Orvella Global Food Trade Insights"
        sub="Practical articles for international buyers sourcing food and agricultural products from India."
      />

      <section className="section-pad">
        <div className="container-x">
          <div className="flex flex-wrap gap-2" data-testid="insights-category-chips">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`chip !px-4 !py-2 transition-colors ${category === c ? "!border-gold !bg-gold/10 !text-gold-dark" : "hover:border-gold"}`}
              >
                {c}
              </button>
            ))}
          </div>

          {(posts || []).length === 0 ? (
            <div className="card mt-10 p-10 text-center" data-testid="insights-empty-state">
              <h2 className="font-serif text-xl font-semibold text-navy">No insights in this category yet</h2>
              <p className="mt-2 text-sm text-mist">New articles are added regularly — check back soon or browse another category.</p>
            </div>
          ) : (
            <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="insights-grid">
              {posts.map((post, i) => (
                <Reveal key={post.id} delay={(i % 3) * 0.05}>
                  <Link to={`/insights/${post.slug}`} className="card card-hover flex h-full flex-col overflow-hidden" data-testid={`insight-card-${post.slug}`}>
                    <div className="aspect-[16/9] overflow-hidden bg-sand">
                      {post.cover_image && <img src={post.cover_image} alt={post.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" />}
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-gold-dark">{post.category}</span>
                      <h2 className="mt-2 font-serif text-lg font-semibold leading-snug text-navy">{post.title}</h2>
                      <p className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-mist">{post.excerpt}</p>
                      <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-navy">
                        Read Insight <ArrowRight size={14} />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
